import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Eye,
  EyeOff,
  Factory,
  LocateFixed,
  Maximize2,
  Move,
  Pencil,
  PencilRuler,
  Users,
} from 'lucide-react';
import { useAuth } from '@/app/AuthProvider';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils';
import { canWrite, isSuperAdmin } from '@/lib/permissions';
import { useProfiles } from '@/features/users/hooks';
import { useCreateWorkstation, useItems, useUpdateWorkstation } from '@/features/inventory/hooks';
import {
  OFFICE_AVATARS_QUERY_KEY,
  persistOfficePosition,
  useMyOfficeAvatar,
  useOfficeAvatars,
  useOfficeLayout,
  useOfficeLayoutRealtime,
  useSaveOfficeAvatar,
  useSaveOfficeLayout,
} from '@/features/digitalOffice/hooks';
import { useLiveStoreVersion, useOfficeRealtime } from '@/features/digitalOffice/presence';
import {
  SPAWN,
  cloneLayout,
  defaultLayout,
  setActiveLayout,
  type OfficeLayout,
} from '@/features/digitalOffice/map';
import {
  setStationSize,
  type LayoutSelection,
  type PlaceKind,
} from '@/features/digitalOffice/layout';
import {
  resolveStationPositions,
  type FloorStation,
  type PinnedWorker,
} from '@/features/digitalOffice/floor';
import { seededAvatar, type AvatarConfig, type Facing } from '@/features/digitalOffice/avatars';
import {
  useConfigureWorkstation,
  useFloorWorkstations,
  useOpenBreaks,
  useOpenSessions,
  useWorkRealtime,
} from '@/features/workMode/hooks';
import { BREAK_REASON_LABEL } from '@/features/workMode/labels';
import { describeWorkError } from '@/features/workMode/api';
import { AvatarCreatorDialog } from '@/components/digitalOffice/AvatarCreatorDialog';
import {
  OfficeCanvas,
  type OfficeCanvasHandle,
  type OfflineCharacter,
} from '@/components/digitalOffice/OfficeCanvas';
import {
  OfficeRoster,
  type RosterBench,
  type RosterPerson,
} from '@/components/digitalOffice/OfficeRoster';
import { LayoutEditorPanel } from '@/components/digitalOffice/LayoutEditorPanel';
import { PingPongLayer } from '@/components/digitalOffice/PongOverlay';
import { usePongSession } from '@/features/digitalOffice/usePongSession';

const EMOTES = ['👋', '👍', '☕', '🎉', '❓', '🍕'];

/** How often we checkpoint the local position to Postgres while walking. */
const PERSIST_INTERVAL_MS = 5000;
/** Polling cadence for persisted positions while Realtime is unavailable. */
const LIVE_FALLBACK_POLL_MS = 4000;
/**
 * Remembered across reloads so a screen recording can't accidentally
 * flash names if the page is refreshed mid-session.
 */
const HIDE_NAMES_STORAGE_KEY = 'atlas:digital-company:hide-names';
const LEGACY_HIDE_NAMES_STORAGE_KEY = 'atlas:floor:hide-names';

function readHideNames(): boolean {
  try {
    return (
      window.localStorage.getItem(HIDE_NAMES_STORAGE_KEY) === '1' ||
      window.localStorage.getItem(LEGACY_HIDE_NAMES_STORAGE_KEY) === '1'
    );
  } catch {
    return false;
  }
}

function writeHideNames(on: boolean) {
  try {
    if (on) window.localStorage.setItem(HIDE_NAMES_STORAGE_KEY, '1');
    else window.localStorage.removeItem(HIDE_NAMES_STORAGE_KEY);
  } catch {
    // localStorage unavailable (private mode etc.) — the toggle still
    // works for this page view, it just won't be remembered.
  }
}

/**
 * /digital-company — Digital Company: a Sims-style virtual 230 Stanford
 * Parkway. Everyone picks a character once, then walks it around the
 * building and sees teammates (online / away / offline) in real time.
 * Workstations from Work Mode are drawn as benches, and anyone signed in
 * at a bench is pinned there with what they are building. Super admins
 * can reshape the building itself — walls, desks, rooms, pallets, machines.
 */
export function FloorPage() {
  const { user, profile, role } = useAuth();
  const canManage = canWrite(role, 'manufacturing');
  const canEditLayout = isSuperAdmin(role);
  const qc = useQueryClient();
  const myAvatar = useMyOfficeAvatar();
  const profiles = useProfiles();
  const save = useSaveOfficeAvatar();
  const [params, setParams] = useSearchParams();
  const layoutQuery = useOfficeLayout();
  useOfficeLayoutRealtime(!params.get('edit'));
  const saveLayout = useSaveOfficeLayout();
  const createStation = useCreateWorkstation();
  const renameStation = useUpdateWorkstation();

  const [editorOpen, setEditorOpen] = useState(false);
  const [showOffline, setShowOffline] = useState(true);
  const [hideNames, setHideNames] = useState(readHideNames);
  const toggleHideNames = () => {
    const next = !hideNames;
    writeHideNames(next);
    setHideNames(next);
  };
  const [rosterOpen, setRosterOpen] = useState(false);
  const [zone, setZone] = useState<string>('');
  const arrangeMode = canManage && params.get('arrange') === '1';
  const canvasRef = useRef<OfficeCanvasHandle | null>(null);

  const setArrangeMode = (on: boolean) => {
    const p = new URLSearchParams(params);
    if (on) p.set('arrange', '1');
    else p.delete('arrange');
    if (on) p.delete('edit');
    setParams(p, { replace: true });
  };

  const editMode = canEditLayout && params.get('edit') === '1';
  const [draft, setDraft] = useState<OfficeLayout | null>(null);
  const [dirty, setDirty] = useState(false);
  const [selection, setSelection] = useState<LayoutSelection | null>(null);
  const [placing, setPlacing] = useState<PlaceKind | null>(null);
  const savedLayout = layoutQuery.data?.layout;
  const layout = draft ?? savedLayout ?? defaultLayout();

  const setEditMode = (on: boolean) => {
    const p = new URLSearchParams(params);
    if (on) {
      p.set('edit', '1');
      p.delete('arrange');
      setDraft(savedLayout ? cloneLayout(savedLayout) : null);
      setDirty(false);
      setSelection(null);
      setPlacing(null);
    } else {
      p.delete('edit');
      if (dirty && draft) {
        saveLayout.mutate(draft, {
          onSettled: () => {
            setDraft(null);
            setDirty(false);
          },
        });
      } else {
        setDraft(null);
        setDirty(false);
      }
      setSelection(null);
      setPlacing(null);
    }
    setParams(p, { replace: true });
  };

  const applyLayout = useCallback((next: OfficeLayout) => {
    setDraft(next);
    setDirty(true);
    setActiveLayout(next);
  }, []);

  useEffect(() => {
    if (!editMode || !dirty || !draft) return;
    const t = window.setTimeout(() => {
      saveLayout.mutate(draft, {
        onSuccess: () => setDirty(false),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Could not save the layout'),
      });
    }, 800);
    return () => window.clearTimeout(t);
    // saveLayout identity changes every render; draft/dirty are the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, dirty, editMode]);

  useEffect(() => {
    if (editMode) return;
    setActiveLayout(savedLayout ?? defaultLayout());
  }, [editMode, savedLayout]);

  const needsAvatar = myAvatar.isSuccess && myAvatar.data === null;
  const config: AvatarConfig | null = myAvatar.data?.config ?? null;
  const initialPosition = useMemo(
    () => myAvatar.data?.position ?? { ...SPAWN, facing: 'down' as Facing },
    // Only the FIRST loaded position matters — later refetches must not
    // teleport the character.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [myAvatar.isSuccess],
  );

  const myName = profile?.full_name?.trim() || profile?.email || 'You';

  const rt = useOfficeRealtime({
    organizationId: profile?.organization_id,
    userId: user?.id,
    name: myName,
    avatarUrl: profile?.avatar_url ?? null,
    config,
    initialPosition,
    enabled: !!config,
  });
  const { store } = rt;
  const storeVersion = useLiveStoreVersion(store);
  const pong = usePongSession({
    userId: user?.id,
    name: myName,
    connected: store.connected,
    sendPong: rt.sendPong,
    subscribePong: rt.subscribePong,
    sendEmote: rt.sendEmote,
  });
  const closePong = pong.close;
  const pongPhase = pong.phase;
  useEffect(() => {
    if ((editMode || arrangeMode) && pongPhase !== 'idle') closePong();
  }, [editMode, arrangeMode, pongPhase, closePong]);

  // While the live channel is down, fall back to polling the persisted
  // positions so teammates still move (at checkpoint cadence) on the map.
  const liveDown = !!config && !store.connected;
  const avatars = useOfficeAvatars({ pollMs: liveDown ? LIVE_FALLBACK_POLL_MS : false });

  // Lets a `move` broadcast that beats presence sync render the right look
  // and name immediately instead of a placeholder.
  const avatarsData = avatars.data;
  const profilesData = profiles.data;
  store.resolveIdentity = useCallback(
    (userId: string) => {
      const p = profilesData?.find((x) => x.id === userId);
      const a = avatarsData?.find((x) => x.userId === userId);
      if (!p && !a) return null;
      return {
        name: p?.full_name?.trim() || p?.email || 'Teammate',
        config: a?.config ?? seededAvatar(userId),
      };
    },
    [profilesData, avatarsData],
  );

  // ── Work Mode: benches + who is at them ──────────────────────────────
  useWorkRealtime(true);
  const workstations = useFloorWorkstations();
  const openSessions = useOpenSessions();
  const openBreaks = useOpenBreaks();
  const items = useItems();
  const configure = useConfigureWorkstation();
  const [optimistic, setOptimistic] = useState<Map<string, { x: number; y: number }>>(new Map());

  const stations: FloorStation[] = useMemo(() => {
    const active = (workstations.data ?? []).filter((s) => s.is_active);
    const sizes = layout.stationSizes;
    return resolveStationPositions(active).map((s) => {
      const o = optimistic.get(s.id);
      const size = sizes?.[s.id];
      const placed = o ? { ...s, x: o.x, y: o.y, placed: true } : s;
      return size ? { ...placed, w: size.w, h: size.h, rotation: size.rotation } : placed;
    });
  }, [workstations.data, optimistic, layout.stationSizes]);

  // Drop optimistic positions once the server agrees with them.
  useEffect(() => {
    if (optimistic.size === 0 || !workstations.data) return;
    let changed = false;
    const next = new Map(optimistic);
    for (const s of workstations.data) {
      const o = next.get(s.id);
      if (
        o &&
        s.floor_x != null &&
        s.floor_y != null &&
        Math.round(s.floor_x) === o.x &&
        Math.round(s.floor_y) === o.y
      ) {
        next.delete(s.id);
        changed = true;
      }
    }
    if (changed) setOptimistic(next);
  }, [workstations.data, optimistic]);

  const handleStationMoved = useCallback(
    (stationId: string, x: number, y: number) => {
      setOptimistic((m) => new Map(m).set(stationId, { x, y }));
      configure.mutate(
        { id: stationId, floor: { x, y } },
        {
          onError: (e) => {
            setOptimistic((m) => {
              const next = new Map(m);
              next.delete(stationId);
              return next;
            });
            toast.error(describeWorkError(e));
          },
        },
      );
    },
    [configure],
  );

  const handleStationResized = useCallback(
    (stationId: string, w: number, h: number, rotation?: number) => {
      applyLayout(setStationSize(layout, stationId, { w, h, rotation }));
    },
    [applyLayout, layout],
  );

  const handleStationRemoved = useCallback(
    (stationId: string) => {
      configure.mutate(
        { id: stationId, floor: null, isActive: false },
        { onError: (e) => toast.error(describeWorkError(e)) },
      );
      applyLayout(setStationSize(layout, stationId, null));
    },
    [applyLayout, configure, layout],
  );

  const handlePlaceStation = useCallback(
    (x: number, y: number) => {
      const used = new Set((workstations.data ?? []).map((s) => s.name.toLowerCase()));
      let n = (workstations.data?.length ?? 0) + 1;
      let name = `Station ${n}`;
      while (used.has(name.toLowerCase())) {
        n += 1;
        name = `Station ${n}`;
      }
      createStation.mutate(
        { name },
        {
          onSuccess: (ws) => {
            configure.mutate(
              { id: ws.id, floor: { x: Math.round(x), y: Math.round(y) } },
              {
                onSuccess: () => setSelection({ layer: 'station', id: ws.id }),
                onError: (e) => toast.error(describeWorkError(e)),
              },
            );
          },
          onError: (err) =>
            toast.error(err instanceof Error ? err.message : 'Could not add workstation'),
        },
      );
    },
    [configure, createStation, workstations.data],
  );

  const handleStationRename = useCallback(
    (id: string, name: string) => {
      renameStation.mutate(
        { id, patch: { name } },
        {
          onError: (err) =>
            toast.error(err instanceof Error ? err.message : 'Could not rename workstation'),
        },
      );
    },
    [renameStation],
  );

  const handleResetLayout = useCallback(() => {
    if (
      !window.confirm(
        'Restore the original 230 Stanford Parkway plan? Your layout edits will be replaced.',
      )
    ) {
      return;
    }
    applyLayout(defaultLayout());
    setSelection(null);
    toast.success('Restored the original plan. Saving…');
  }, [applyLayout]);

  const workers: PinnedWorker[] = useMemo(() => {
    const byId = new Map((avatars.data ?? []).map((a) => [a.userId, a]));
    const profileById = new Map((profiles.data ?? []).map((p) => [p.id, p]));
    const itemById = new Map((items.data ?? []).map((i) => [i.id, i]));
    const breakBySession = new Map((openBreaks.data ?? []).map((b) => [b.session_id, b]));
    const stationById = new Map(stations.map((s) => [s.id, s]));
    const out: PinnedWorker[] = [];
    for (const s of openSessions.data ?? []) {
      const station = stationById.get(s.workstation_id);
      if (!station) continue;
      const p = profileById.get(s.user_id);
      const brk = breakBySession.get(s.id);
      out.push({
        userId: s.user_id,
        name: p?.full_name?.trim() || p?.email || 'Teammate',
        config: byId.get(s.user_id)?.config ?? seededAvatar(s.user_id),
        stationId: station.id,
        stationName: station.name,
        x: station.x,
        y: station.y,
        itemName: s.item_id ? (itemById.get(s.item_id)?.name ?? null) : null,
        unitsDone: s.units_completed,
        unitStartedAt: Date.parse(s.unit_started_at),
        onBreak: !!brk,
        breakLabel: brk ? (BREAK_REASON_LABEL[brk.reason] ?? 'On a break') : null,
      });
    }
    return out;
  }, [avatars.data, profiles.data, items.data, openBreaks.data, openSessions.data, stations]);

  const benches: RosterBench[] = useMemo(
    () =>
      workers
        .map((w) => ({
          userId: w.userId,
          name: w.name,
          config: w.config,
          stationName: w.stationName,
          itemName: w.itemName,
          unitsDone: w.unitsDone,
          unitStartedAt: w.unitStartedAt,
          onBreak: w.onBreak,
          breakLabel: w.breakLabel,
        }))
        .sort((a, b) => a.stationName.localeCompare(b.stationName)),
    [workers],
  );

  // ── Position persistence (slow cadence) ──────────────────────────────
  const lastPersistRef = useRef(0);
  const dirtyRef = useRef(false);
  const handleMove = useCallback(
    (x: number, y: number, facing: Facing, moving: boolean, speed: number) => {
      rt.sendMove(x, y, facing, moving, speed);
      dirtyRef.current = true;
      const now = Date.now();
      if (user?.id && (!moving || now - lastPersistRef.current > PERSIST_INTERVAL_MS)) {
        lastPersistRef.current = now;
        dirtyRef.current = false;
        void persistOfficePosition(user.id, { x, y, facing });
      }
    },
    // rt.sendMove is a fresh closure each render but only touches refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id],
  );

  // Checkpoint when leaving the page / closing the tab.
  useEffect(() => {
    const flush = () => {
      if (!dirtyRef.current || !user?.id) return;
      const pos = canvasRef.current?.getPosition();
      if (pos) {
        dirtyRef.current = false;
        void persistOfficePosition(user.id, pos);
      }
    };
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [user?.id]);

  // When someone leaves, their final position lands in the DB shortly
  // after — refresh the offline snapshot so the silhouette is accurate.
  const prevCountRef = useRef(0);
  useEffect(() => {
    const count = store.players.size;
    if (count < prevCountRef.current) {
      const t = window.setTimeout(
        () => qc.invalidateQueries({ queryKey: OFFICE_AVATARS_QUERY_KEY }),
        2000,
      );
      prevCountRef.current = count;
      return () => window.clearTimeout(t);
    }
    prevCountRef.current = count;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeVersion]);

  // ── Roster + offline silhouettes ─────────────────────────────────────
  const people: RosterPerson[] = useMemo(() => {
    const byId = new Map((avatars.data ?? []).map((a) => [a.userId, a]));
    return (profiles.data ?? [])
      .filter((p) => p.is_active && p.role !== 'external_lender')
      .map((p) => {
        const rec = byId.get(p.id);
        const live = store.lastKnown.get(p.id);
        const position = rec
          ? live && (!rec.lastSeenAt || live.at > new Date(rec.lastSeenAt).getTime())
            ? { x: live.x, y: live.y, facing: live.facing }
            : rec.position
          : null;
        return {
          userId: p.id,
          name: p.full_name?.trim() || p.email,
          jobTitle: p.job_title,
          config: rec?.config ?? null,
          position,
          lastSeenAt: live ? new Date(live.at).toISOString() : (rec?.lastSeenAt ?? null),
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatars.data, profiles.data, storeVersion]);

  const offline: OfflineCharacter[] = useMemo(
    () =>
      people
        .filter(
          (
            p,
          ): p is RosterPerson & {
            config: AvatarConfig;
            position: NonNullable<RosterPerson['position']>;
          } => p.userId !== user?.id && !!p.config && !!p.position,
        )
        .map((p) => ({
          userId: p.userId,
          name: p.name,
          config: p.config,
          x: p.position.x,
          y: p.position.y,
          facing: p.position.facing,
        })),
    [people, user?.id],
  );

  const handleSaveAvatar = (next: AvatarConfig) => {
    save.mutate(
      {
        config: next,
        position: canvasRef.current?.getPosition() ?? myAvatar.data?.position,
      },
      {
        onSuccess: () => {
          setEditorOpen(false);
          toast.success(config ? 'Character updated' : 'Welcome to Digital Company');
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Could not save character'),
      },
    );
  };

  const focusPlayer = (userId: string) => {
    canvasRef.current?.focusPlayer(userId);
    setRosterOpen(false);
  };

  useEffect(() => {
    if (!editMode || draft) return;
    if (savedLayout) setDraft(cloneLayout(savedLayout));
  }, [editMode, savedLayout, draft]);

  // Jump to the benches when arriving in arrange mode so the handles are in view.
  const arrangedOnceRef = useRef(false);
  useEffect(() => {
    if (!arrangeMode || arrangedOnceRef.current || !config || stations.length === 0) return;
    arrangedOnceRef.current = true;
    const t = window.setTimeout(() => canvasRef.current?.focusStation(stations[0].id), 50);
    return () => window.clearTimeout(t);
  }, [arrangeMode, config, stations]);

  const editedOnceRef = useRef(false);
  useEffect(() => {
    if (!editMode) {
      editedOnceRef.current = false;
      return;
    }
    if (editedOnceRef.current || !config) return;
    editedOnceRef.current = true;
    const t = window.setTimeout(() => canvasRef.current?.fitWorld(), 50);
    return () => window.clearTimeout(t);
  }, [editMode, config]);

  // Freeze walking while a modal is up. The character editor must not share
  // that flag with a Pong match: starting a game was opening the editor, and
  // its close button could not dismiss it while the match was still running.
  const avatarOpen = needsAvatar || editorOpen;
  const walkLocked = avatarOpen || pong.blocking;
  const onlineCount = store.players.size + (config ? 1 : 0);

  return (
    <div className="flex flex-col">
      <PageHeader
        eyebrow="Overview / Digital Company"
        title="Digital Company"
        meta={
          <span className="inline-flex flex-wrap items-center gap-x-2">
            <Factory className="h-3.5 w-3.5" strokeWidth={1.6} />
            230 Stanford Parkway
            {workers.length > 0 && (
              <>
                <span aria-hidden>·</span>
                <span>{workers.length} on the benches</span>
              </>
            )}
            {zone && (
              <>
                <span aria-hidden>·</span>
                <span>You’re in the {zone.toLowerCase()}</span>
              </>
            )}
          </span>
        }
        actions={
          <>
            <Button
              variant={hideNames ? 'default' : 'outline'}
              size="sm"
              role="switch"
              aria-checked={hideNames}
              onClick={toggleHideNames}
              title={
                hideNames
                  ? 'Names are hidden on the map and in the panel — click to show them'
                  : 'Hide employee names on the map and in the panel (for screen recordings)'
              }
            >
              {hideNames ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">{hideNames ? 'Names hidden' : 'Hide names'}</span>
            </Button>
            <StatusToggle status={rt.status} onChange={rt.setStatus} disabled={!config} />
            {canEditLayout && (
              <Button
                variant={editMode ? 'default' : 'outline'}
                size="sm"
                onClick={() => setEditMode(!editMode)}
                title="Move, resize, remove or add walls, desks, rooms, pallets and machines"
              >
                <PencilRuler className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {editMode ? 'Done editing' : 'Edit layout'}
                </span>
              </Button>
            )}
            {canManage && !editMode && (
              <Button
                variant={arrangeMode ? 'default' : 'outline'}
                size="sm"
                onClick={() => setArrangeMode(!arrangeMode)}
                title="Drag benches to where they really are"
              >
                <Move className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {arrangeMode ? 'Done arranging' : 'Arrange benches'}
                </span>
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => canvasRef.current?.fitWorld()}
              title="Show the whole lot"
            >
              <Maximize2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Fit view</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => canvasRef.current?.followMe()}
              title="Centre on me"
            >
              <LocateFixed className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Find me</span>
            </Button>
            <Button size="sm" onClick={() => setEditorOpen(true)} disabled={!config}>
              <Pencil className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Edit character</span>
            </Button>
          </>
        }
      />

      <div className="flex min-h-[520px] gap-4" style={{ height: 'calc(100vh - 230px)' }}>
        {/* Map */}
        <div className="relative min-w-0 flex-1 overflow-hidden rounded-xl border border-border bg-[#B9C9A2] shadow-sm">
          {myAvatar.isLoading || !user || !profile ? (
            <Skeleton className="h-full w-full rounded-none" />
          ) : config ? (
            <OfficeCanvas
              ref={canvasRef}
              me={{ userId: user.id, name: myName, config, status: rt.status }}
              initialPosition={initialPosition}
              store={store}
              offline={offline}
              showOffline={showOffline}
              inputEnabled={!walkLocked && !editMode}
              onMove={handleMove}
              onZoneChange={setZone}
              stations={stations}
              workers={workers}
              arrangeMode={arrangeMode}
              onStationMoved={handleStationMoved}
              hideNames={hideNames}
              layout={layout}
              editMode={editMode}
              selection={selection}
              onSelect={setSelection}
              onLayoutChange={applyLayout}
              placing={placing}
              onPlacingChange={setPlacing}
              onPlaceStation={handlePlaceStation}
              onStationResized={handleStationResized}
              onStationRemoved={handleStationRemoved}
              onPingPongNear={pong.setNear}
            />
          ) : (
            <div className="flex h-full items-center justify-center bg-[radial-gradient(ellipse_at_center,_#D9E3C6_0%,_#B9C9A2_100%)]">
              <p className="rounded-full bg-white/80 px-4 py-2 text-sm text-muted-foreground shadow-sm">
                Create your character to step inside.
              </p>
            </div>
          )}

          {config && (
            <>
              {store.error && (
                <div
                  role="status"
                  title={store.error}
                  className="pointer-events-none absolute left-1/2 top-3 max-w-[90%] -translate-x-1/2 rounded-full bg-amber-50/95 px-3 py-1.5 text-center text-[11px] text-amber-800 shadow-sm ring-1 ring-amber-200 backdrop-blur"
                >
                  Live updates paused — showing last saved positions while we reconnect.
                </div>
              )}

              {/* Mobile roster toggle */}
              <button
                type="button"
                onClick={() => setRosterOpen((o) => !o)}
                className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-medium text-humus shadow-sm backdrop-blur lg:hidden"
              >
                <Users className="h-3.5 w-3.5" strokeWidth={1.8} />
                {onlineCount + workers.length}
              </button>

              <PingPongLayer
                session={pong}
                connected={store.connected}
                enabled={!editMode && !arrangeMode}
              />

              {/* Emote bar */}
              <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-white/90 p-1 shadow-sm backdrop-blur">
                {EMOTES.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => rt.sendEmote(e)}
                    title="Send a reaction"
                    className="flex h-8 w-8 items-center justify-center rounded-full text-base transition-transform hover:scale-110 hover:bg-secondary active:scale-95"
                  >
                    {e}
                  </button>
                ))}
              </div>

              {/* Slide-over roster on small screens */}
              {rosterOpen && user && (
                <div className="absolute inset-y-3 right-3 w-80 max-w-[85%] lg:hidden">
                  {editMode ? (
                    <LayoutEditorPanel
                      layout={layout}
                      selection={selection}
                      placing={placing}
                      stations={stations}
                      saving={saveLayout.isPending}
                      dirty={dirty}
                      onPlacingChange={setPlacing}
                      onLayoutChange={applyLayout}
                      onSelect={setSelection}
                      onStationRename={handleStationRename}
                      onStationResize={handleStationResized}
                      onStationRemove={handleStationRemoved}
                      onResetDefault={handleResetLayout}
                      className="shadow-lg"
                    />
                  ) : (
                    <OfficeRoster
                      store={store}
                      meId={user.id}
                      myName={myName}
                      myConfig={config}
                      myStatus={rt.status}
                      people={people}
                      benches={benches}
                      showOffline={showOffline}
                      onToggleOffline={setShowOffline}
                      onFocus={focusPlayer}
                      hideNames={hideNames}
                      className="shadow-lg"
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Roster or layout editor (desktop) */}
        {config && user && (
          <div className="hidden w-80 shrink-0 lg:block">
            {editMode ? (
              <LayoutEditorPanel
                layout={layout}
                selection={selection}
                placing={placing}
                stations={stations}
                saving={saveLayout.isPending}
                dirty={dirty}
                onPlacingChange={setPlacing}
                onLayoutChange={applyLayout}
                onSelect={setSelection}
                onStationRename={handleStationRename}
                onStationResize={handleStationResized}
                onStationRemove={handleStationRemoved}
                onResetDefault={handleResetLayout}
              />
            ) : (
              <OfficeRoster
                store={store}
                meId={user.id}
                myName={myName}
                myConfig={config}
                myStatus={rt.status}
                people={people}
                benches={benches}
                showOffline={showOffline}
                onToggleOffline={setShowOffline}
                onFocus={focusPlayer}
                hideNames={hideNames}
                footer={
                  canManage ? (
                    <Link
                      to="/mfg/workstations"
                      className="text-[11px] text-info underline-offset-4 hover:underline"
                    >
                      Open Workstations for the full picture →
                    </Link>
                  ) : undefined
                }
              />
            )}
          </div>
        )}
      </div>

      {user && (
        <AvatarCreatorDialog
          open={avatarOpen}
          initial={config}
          seed={user.id}
          required={needsAvatar}
          saving={save.isPending}
          onSave={handleSaveAvatar}
          onOpenChange={(open) => {
            if (!open) setEditorOpen(false);
          }}
        />
      )}
    </div>
  );
}

function StatusToggle({
  status,
  onChange,
  disabled,
}: {
  status: 'online' | 'away';
  onChange: (s: 'online' | 'away') => void;
  disabled?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Presence status"
      className={cn(
        'inline-flex h-8 items-center rounded-full border border-border bg-background p-0.5 text-xs',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      {(['online', 'away'] as const).map((s) => (
        <button
          key={s}
          type="button"
          role="radio"
          aria-checked={status === s}
          onClick={() => onChange(s)}
          className={cn(
            'inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 transition-colors',
            status === s
              ? 'bg-secondary text-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              s === 'online' ? 'bg-emerald-400' : 'bg-amber-400',
            )}
          />
          {s === 'online' ? 'Online' : 'Away'}
        </button>
      ))}
    </div>
  );
}
